import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PetFormSection } from "@/components/owners/PetFormSection";
import { CustomIcons } from "@/components/icons/CustomIcons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";

import { usePet, useUpdatePet, useDeletePet } from "@/hooks/usePets";
import {
  useUploadPetPhoto,
  useDeletePetPhoto,
} from "@/hooks/useUpload";
import type { Tables } from "@/integrations/supabase/types";
import { useSmartBack } from "@/lib/navigation-utils";
import type { PetInput } from "@/lib/validations/pet";
import { createPetWizardSchema } from "@/lib/validations/petWizard";
import { getLatestPetWeight } from "@/lib/utils/pet-weight";
import {
  cancelFutureAppointmentsForPet,
  fetchFutureAppointmentsForPet,
  isPetDeceased,
  useDeceasedPetStatusId,
} from "@/lib/utils/pet-death";

type PetConditionLink = {
  id?: string;
  condition?: { id?: string | null } | null;
};

export default function EditPet() {
  const { t } = useTranslation("patients");
  const smartBack = useSmartBack();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: pet, isLoading, error } = usePet(id ?? "");
  const updatePet = useUpdatePet();
  const deletePet = useDeletePet();
  const uploadPhoto = useUploadPetPhoto();
  const deletePhoto = useDeletePetPhoto();

  const [hasInitialized, setHasInitialized] = useState(false);
  const [initialPhotoUrl, setInitialPhotoUrl] = useState<string | null>(null);
  const deceasedStatusId = useDeceasedPetStatusId();
  const petWizardSchema = useMemo(
    () => createPetWizardSchema(deceasedStatusId),
    [deceasedStatusId],
  );

  const form = useForm<PetInput>({
    resolver: zodResolver(petWizardSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      name: "",
      owner_id: "",
      record_number: "",
      gender_id: "",
      sterilization_id: "",
      birth_date: "",
      species_id: "",
      breed_id: "",
      size_category_id: "",
      coat_type_id: "",
      color_id: "",
      status_id: "",
      death_date: "",
      death_cause: "",
      microchip_number: "",
      health_plan: "",
      pedigree_number: "",
      notes: "",
      current_weight: undefined,
      allergies: [],
      temperaments: [],
      photo: null,
      attachments: [],
    },
  });

  // Persist current photo path for delete logic.
  useEffect(() => {
    const photoFilePath = Array.isArray(pet?.photo)
      ? pet.photo[0]?.file_path
      : pet?.photo?.file_path;
    setInitialPhotoUrl(photoFilePath ?? null);
  }, [pet?.photo]);

  // Preload form from existing pet.
  useEffect(() => {
    if (!pet || hasInitialized) return;

    const nextAllergies = (pet.allergies ?? [])
      .map((item) => {
        const typed = item as PetConditionLink;
        return typed?.condition?.id ?? typed?.id;
      })
      .filter((conditionId): conditionId is string => typeof conditionId === "string" && Boolean(conditionId));

    const nextTemperaments = (pet.temperaments ?? [])
      .map((item) => {
        const typed = item as PetConditionLink;
        return typed?.condition?.id ?? typed?.id;
      })
      .filter((temperamentId): temperamentId is string => typeof temperamentId === "string" && Boolean(temperamentId));

    const photoPath = Array.isArray(pet.photo) ? pet.photo[0]?.file_path : pet.photo?.file_path;
    const latestWeight = getLatestPetWeight(pet.weights ?? []);
    const latestWeightValue =
      typeof latestWeight?.weight === "number" ? latestWeight.weight : undefined;

    form.reset({
      name: pet.name ?? "",
      owner_id: pet.owner_id ?? "",
      record_number: pet.record_number ?? "",
      gender_id: pet.gender_id ?? "",
      sterilization_id: pet.sterilization_id ?? "",
      birth_date: pet.birth_date ?? "",
      species_id: pet.species_id ?? "",
      breed_id: pet.breed_id ?? "",
      size_category_id: pet.size_category_id ?? "",
      coat_type_id: pet.coat_type_id ?? "",
      color_id: pet.color_id ?? "",
      status_id: pet.status_id ?? "",
      death_date: pet.death_date ?? "",
      death_cause: pet.death_cause ?? "",
      microchip_number: pet.microchip_number ?? "",
      health_plan: pet.health_plan ?? "",
      pedigree_number: pet.pedigree_number ?? "",
      notes: pet.notes ?? "",
      current_weight: latestWeightValue,
      allergies: nextAllergies,
      temperaments: nextTemperaments,
      photo: photoPath ?? null,
      attachments: [],
    });

    setHasInitialized(true);
  }, [pet, form, hasInitialized]);

  const onSubmit = async (data: PetInput) => {
    console.log("onSubmit function called!");
    if (!id) {
      console.log("No id, returning early");
      return;
    }

    // Obter o valor atual da foto diretamente do formulário para garantir que temos o valor mais recente
    const currentPhoto = form.getValues("photo");

    // Debug: verificar valores
    console.log("onSubmit - data.photo:", data.photo, "type:", typeof data.photo, "isFile:", data.photo instanceof File);
    console.log("onSubmit - currentPhoto:", currentPhoto, "type:", typeof currentPhoto, "isFile:", currentPhoto instanceof File);
    console.log("onSubmit - initialPhotoUrl:", initialPhotoUrl);

    const { photo, attachments: _attachments, ...rest } = data;
    const latestWeight = getLatestPetWeight(pet?.weights ?? []);
    const latestWeightValue =
      typeof latestWeight?.weight === "number" ? latestWeight.weight : undefined;
    const submittedWeight =
      typeof rest.current_weight === "number" ? rest.current_weight : undefined;
    const currentWeight =
      submittedWeight !== undefined && submittedWeight !== latestWeightValue
        ? submittedWeight
        : undefined;
    const payload: PetInput = {
      ...rest,
      current_weight: currentWeight,
    };

    const isMarkingAsDeceased =
      !isPetDeceased(pet) &&
      Boolean(data.death_date);

    if (isMarkingAsDeceased) {
      const futureAppointments = await fetchFutureAppointmentsForPet(id);
      if (futureAppointments.length > 0) {
        const shouldCancel = window.confirm(
          t("petForm.death.confirmFutureAppointments", { count: futureAppointments.length }),
        );
        if (shouldCancel) {
          await cancelFutureAppointmentsForPet(id);
        }
      }
    }

    await updatePet.mutateAsync({ id, input: payload });

    // Usar currentPhoto em vez de photo do data para garantir que temos o valor atualizado
    const photoToProcess = currentPhoto ?? photo;
    const shouldDeletePhoto = !photoToProcess && Boolean(initialPhotoUrl);

    console.log("onSubmit - photoToProcess:", photoToProcess, "isFile:", photoToProcess instanceof File);
    console.log("onSubmit - shouldDeletePhoto:", shouldDeletePhoto);

    if (shouldDeletePhoto) {
      console.log("Deletando foto...");
      await deletePhoto.mutateAsync(id);
    } else if (photoToProcess instanceof File) {
      console.log("Fazendo upload da nova foto...");
      await uploadPhoto.mutateAsync({ petId: id, file: photoToProcess });
    } else {
      console.log("Nenhuma ação de foto necessária");
    }

    smartBack(`/patients/pets/${id}`);
  };

  const isSaving =
    updatePet.isPending ||
    uploadPhoto.isPending ||
    deletePhoto.isPending;

  if (!id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack("/patients")} aria-label={t("editPet.backToPatients")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">{t("editPet.notFound")}</h1>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack("/patients")} aria-label={t("editPet.backToPatients")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-12 w-full" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack("/patients")} aria-label={t("editPet.backToPatients")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">{t("editPet.loadErrorTitle")}</h1>
        </div>
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground">
              {t("editPet.loadErrorDescription")}
            </p>
            <Button className="mt-4" onClick={() => smartBack("/patients")}>
              {t("editPet.backToPatients")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => smartBack(`/patients/pets/${id}`)}
            aria-label={t("editPet.backToPatient")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t("editPet.title")}</h1>
            <p className="text-muted-foreground mt-1">{t("editPet.description")}</p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form
          onSubmit={(e) => {
            console.log("Form submit event triggered");
            console.log("Form errors:", form.formState.errors);
            console.log("Form values:", form.getValues());
            form.handleSubmit(
              (data) => {
                console.log("handleSubmit success callback called");
                onSubmit(data);
              },
              (errors) => {
                console.log("handleSubmit error callback called - validation failed");
                console.log("Validation errors:", errors);
              }
            )(e);
          }}
          className="space-y-4"
        >
          <Card>
            <CardHeader>
              <CardTitle>{t("editPet.sections.patientData")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <PetFormSection
                form={form}
                index={0}
                photoAllowReplace
                petId={id}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button type="button" variant="outline" className="shadow-soft-search" asChild>
              <Link to={`/patients/pets/${id}`}>
                <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
                {t("editPet.actions.cancel")}
              </Link>
            </Button>

            <Button type="submit" className="w-full sm:w-auto" disabled={isSaving}>
              <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
              <span className="font-normal">{isSaving ? t("editPet.actions.saving") : t("editPet.actions.save")}</span>
            </Button>
          </div>

          <div className="border-t pt-6">
            <div className="flex flex-col gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4 ">
              <div className="space-y-1">
                <p className="font-medium text-foreground">{t("editPet.delete.title")}</p>
                <p className="text-sm text-muted-foreground">{t("editPet.delete.description")}</p>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    className="w-fit"
                    disabled={deletePet.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                    {deletePet.isPending ? t("editPet.delete.pending") : t("editPet.delete.button")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("editPet.delete.title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("editPet.delete.description")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={deletePet.isPending}>{t("editPet.actions.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        await deletePet.mutateAsync(id);
                        navigate("/patients", { replace: true });
                      }}
                      disabled={deletePet.isPending}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t("editPet.delete.confirm")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
